// A "blast" is the controlled, canary-then-ramp send of one email touchpoint to its target audience.
//
// Shapes are generated from OrbitalX.Dto — see api-generated.d.ts. State values are the C# enum NAMES
// (ORB-357), and the four unions below are narrowed here because the C# DTOs declare them `string`.

import type {
  CampaignBlastSummaryDto as GeneratedCampaignBlastSummaryDto,
  CampaignEmailRollupDto as GeneratedCampaignEmailRollupDto,
  EmailBlastGateDecisionDto as GeneratedEmailBlastGateDecisionDto,
  EmailBlastSplitTestSkipDto as GeneratedEmailBlastSplitTestSkipDto,
  EmailBlastStateChangeDto as GeneratedEmailBlastStateChangeDto,
  EmailBlastStatsDto,
  EmailBlastViewDto as GeneratedEmailBlastViewDto,
} from './api-generated'

export type {
  BlastEngagementDto,
  BlastRecipientDto,
  BlastRecipientsPageDto,
  ClickedLinkDto,
  EmailBlastStatsDto,
  EmailBlastVariantResultDto,
  EmailEngagementTrendPointDto,
  EmailBlastSendingDomainPlanDto,
  EmailBlastSendingDomainDto,
  EmailBlastDaySentDto,
  EmailBlastWarmupForecastDto,
  EmailBlastWarmupForecastDayDto,
} from './api-generated'

// Lifecycle of a blast. Drives the State badge and which controls are offered.
export type EmailBlastState =
  | 'Canary'      // initial small sample is sending
  | 'Measuring'   // canary sent; holding to measure bounce/complaint rates
  | 'Releasing'   // gates passed; ramping the remaining audience in stages
  | 'Paused'      // breaker tripped (or manually paused) — resumable
  | 'Complete'    // whole audience sent
  | 'Failed'      // unrecoverable error
  | 'Cancelled'   // operator-cancelled

// Terminal states accept no further actions (Cancel is hidden, Resume never applies).
export const TERMINAL_BLAST_STATES: EmailBlastState[] = ['Complete', 'Failed', 'Cancelled']

export function isTerminalBlastState(state: EmailBlastState): boolean {
  return TERMINAL_BLAST_STATES.includes(state)
}

/** The gate a paused blast tripped on. Mirrors EmailBlastTripMetric's description strings. */
export type BlastTripMetric = 'HardBounce' | 'Complaint' | 'DeliveryRate' | 'MinSendFloor'

// Why a configured A/B split test didn't run (ORB-472). The counts are the ones the skip was decided on,
// so the monitor can explain the reason rather than just name it.
export type SplitTestSkipReason = 'BelowPerVariantFloor' | 'NoChallengerSubject' | 'NoRemainder' | 'RecipientLocal'

// One transition recorded on the blast. The C# type is EmailBlastStateChangeDto.
export interface EmailBlastStateHistoryEntryDto extends Omit<GeneratedEmailBlastStateChangeDto, 'State'> {
  State: EmailBlastState
}

/**
 * A circuit-breaker evaluation at a sampling checkpoint. `Decision` is the breaker's call; when it
 * Pauses, `Reason` is the tripped condition (e.g. "Hard bounce 6.2% over 5% limit").
 *
 * Read `TripMetric` rather than substring-matching `Reason`, which is server prose and free to change
 * (ORB-706). It is null when the decision did not pause the send.
 */
export interface EmailBlastGateDecisionDto extends Omit<GeneratedEmailBlastGateDecisionDto, 'TripMetric'> {
  TripMetric?: BlastTripMetric | null
}

/**
 * `EligibleCount` is eligible recipients at blast creation after the eligibility filters; `CohortSize`
 * is what the configured percent resolved to (0 for non-count reasons); `RequiredPerVariant` is the
 * per-variant floor in force for this environment at create; `VariantCount` is control + challengers.
 */
export interface EmailBlastSplitTestSkipDto extends Omit<GeneratedEmailBlastSplitTestSkipDto, 'Reason'> {
  Reason: SplitTestSkipReason
}

/**
 * Full view of a blast: config snapshot (the gate thresholds + ramp plan captured at launch), live
 * stats, the state/gate history, and lifecycle timing.
 *
 * `PulledAfterRegistrationCount` is recipients pulled after staging because they registered during the
 * window (ORB-409); live recipients = TotalAudience − this. `MinCanaryDeliveryPercent` is a FLOOR
 * checked on the canary only — below it is the failure, so it must not be rendered like the two
 * ceilings above it. `RampStagePercents` is normalized server-side since ORB-706: ascending and ending
 * at 100, with a single [100] meaning one un-gated drain. `Subject` is read off the send rows, so it is
 * null until the blast has any. `StatsAsOfUtc` is set on the DETAIL, which recounts from the send rows,
 * and null on the list, which reads the persisted snapshot. `Engagement` is null on write/monitor paths
 * that don't request it; on the LIST only counts + rates are filled, on the DETAIL the top links and
 * approximate unsubscribe are too. `SplitTestSkip` distinguishes "no test was ever configured"
 * (IsSplitTest false, null) from "one was, and was skipped" (ORB-472).
 */
export interface EmailBlastViewDto
  extends Omit<GeneratedEmailBlastViewDto,
    'State' | 'StateHistory' | 'WinMetric' | 'GateDecisions' | 'SplitTestSkip'> {
  State: EmailBlastState
  StateHistory: EmailBlastStateHistoryEntryDto[]
  WinMetric?: 'Opens' | 'Clicks' | null
  GateDecisions: EmailBlastGateDecisionDto[]
  SplitTestSkip?: EmailBlastSplitTestSkipDto | null
}

/** One blast's line in the campaign rollup table. Rates null when the blast has delivered nothing. */
export interface CampaignBlastSummaryDto extends Omit<GeneratedCampaignBlastSummaryDto, 'State'> {
  State: EmailBlastState
}

/**
 * Campaign-grain rollup across the campaign's marketing blasts. Counts are send-grain; unsubscribe is
 * distinct-recipient. Rates are over delivered (null when nothing delivered).
 */
export interface CampaignEmailRollupDto extends Omit<GeneratedCampaignEmailRollupDto, 'Blasts'> {
  Blasts: CampaignBlastSummaryDto[]
}

/** Rows that have ever left the queue (dispatched). The pre-send guard keys "has it started?" on this
 *  being zero, and the lock banner uses it for send progress. Skipped (suppressed) rows never dispatch.
 *
 *  Stats.Sent IS the dispatched-ever count — it counts rows carrying a SentAtUtc stamp, which is set at
 *  dispatch and never cleared. Delivered / Bounced / Complained / Failed PARTITION it, so the old
 *  Sent + Delivered + ... formula counted every settled row twice (ORB-706). It only ever looked right
 *  because both call sites test it against zero. */
export function dispatchedCount(stats: EmailBlastStatsDto): number {
  return stats.Sent
}

/** Rows the blast has finished with, one way or another: dispatched, cancelled before send, or failed
 *  to render. The right numerator for a progress bar denominated on the whole audience — a skipped row
 *  is settled, never dispatches, and would otherwise leave the bar permanently short of 100% on any
 *  blast with a suppression (ORB-706). */
export function resolvedCount(stats: EmailBlastStatsDto): number {
  return stats.Sent + stats.Skipped + stats.Failed
}

// State pill colors — keyed by the C# enum NAME. In-progress states read neutral/active,
// Paused reads as a warning, Failed/Cancelled as danger, Complete as success.
// (Mirrors the DOMAIN_STATUS_COLORS idiom in sendingDomains.ts rather than reusing it.)
export const BLAST_STATE_COLORS: Record<EmailBlastState, string> = {
  Canary: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
  Measuring: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-300',
  Releasing: 'bg-sky-100 text-sky-800 dark:bg-sky-900/30 dark:text-sky-300',
  Paused: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300',
  Complete: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
  Failed: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
  Cancelled: 'bg-slate-200 text-slate-700 dark:bg-slate-700 dark:text-slate-200',
}

// Releasing reads "Sending" (ORB-953): once a warming domain spreads a broadcast over days, "Releasing"
// describes the engine's gate rather than what the operator sees happening.
export const BLAST_STATE_LABELS: Record<EmailBlastState, string> = {
  Canary: 'Canary',
  Measuring: 'Measuring',
  Releasing: 'Sending',
  Paused: 'Paused',
  Complete: 'Complete',
  Failed: 'Failed',
  Cancelled: 'Cancelled',
}

// Short, human explanation of each state for the detail header.
export const BLAST_STATE_DESCRIPTIONS: Record<EmailBlastState, string> = {
  Canary: 'Sending the initial canary sample.',
  Measuring: 'Canary sent — measuring bounce and complaint rates before releasing the rest.',
  Releasing: 'Gates passed — ramping the remaining audience in stages.',
  Paused: 'Send is paused. Resume to continue, or cancel to stop it.',
  Complete: 'The full audience has been sent.',
  Failed: 'The send failed and cannot continue.',
  Cancelled: 'The send was cancelled.',
}
